import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, CheckCircle, XCircle, CheckSquare, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../../api/axios';
import { useLoading } from '../../../context/LoadingContext';
import styles from './Payroll.module.css';
import Sidebar from '../../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../../components/Common/Footer/Footer';

const ViewPayroll = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [payroll, setPayroll] = useState(null);
  const [meta, setMeta] = useState(null);

  // Modals for actions
  const [actionModal, setActionModal] = useState({ open: false, type: '', remarks: '' });
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

  const fetchPayroll = useCallback(async () => {
    showLoader();
    try {
      const response = await api.get(`/payroll/${id}`);
      if (response.data.success) {
        setPayroll(response.data.data);
        setMeta(response.data.meta);
      }
    } catch (error) {
      toast.error('Failed to fetch payroll details');
      navigate('/employees/payroll');
    } finally {
      hideLoader();
    }
  }, [id, navigate, showLoader, hideLoader]);

  useEffect(() => {
    fetchPayroll();
  }, [fetchPayroll]);

  const handleAction = async () => {
    if (!actionModal.type) return;
    showLoader();
    try {
      const response = await api.post(`/payroll/${id}/${actionModal.type}`, {
        remarks: actionModal.remarks
      });
      if (response.data.success) {
        toast.success(`Payroll ${actionModal.type}d successfully`);
        setActionModal({ open: false, type: '', remarks: '' });
        fetchPayroll();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${actionModal.type} payroll`);
    } finally {
      hideLoader();
    }
  };

  const handlePrint = async () => {
    try {
      showLoader();
      const response = await api.get(`/payroll/${id}/pdf/preview`, { responseType: 'blob' });
      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      setPdfPreviewUrl(fileURL);
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to open PDF preview');
    } finally {
      hideLoader();
    }
  };

  const closePdfPreview = () => {
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl(null);
    }
  };

  const handleDownloadPdf = () => {
    if (pdfPreviewUrl) {
      const link = document.createElement('a');
      link.href = pdfPreviewUrl;
      link.setAttribute('download', `Salary_Slip_${payroll?.payroll_number || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  if (!payroll || !meta) return null;

  return (
    <div className={styles['payroll-container']}>
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      
      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />
        
        <main className={styles['main-content']}>
          <Breadcrumbs customItems={[
            { label: 'HR', path: '#' },
            { label: 'Payroll / Salary', path: '/employees/payroll' },
            { label: 'View Payslip', path: '#' }
          ]} />

          <div>
            {/* Header Actions */}
            <div className={`no-print ${styles['payslip-header-actions']}`}>
              <button 
                onClick={() => navigate('/employees/payroll')} 
                className={styles['payslip-back-btn']}
              >
                <ArrowLeft size={20} /> Back to Payroll
              </button>
              <div className={styles['payslip-action-buttons']}>
                {meta.can_approve && (
                  <button 
                    className={styles['payslip-btn-approve']}
                    onClick={() => setActionModal({ open: true, type: 'approve', remarks: '' })}
                  >
                    <CheckCircle size={18} /> Approve
                  </button>
                )}
                {meta.can_mark_paid && (
                  <button 
                    className={styles['payslip-btn-paid']} 
                    onClick={() => setActionModal({ open: true, type: 'mark-paid', remarks: '' })}
                  >
                    <CheckSquare size={18} /> Mark as Paid
                  </button>
                )}
                {meta.can_cancel && (
                  <button 
                    className={styles['payslip-btn-cancel']} 
                    onClick={() => setActionModal({ open: true, type: 'cancel', remarks: '' })}
                  >
                    <XCircle size={18} /> Cancel
                  </button>
                )}
                <button 
                  className={styles['payslip-btn-print']} 
                  onClick={handlePrint}
                >
                  <Printer size={18} /> Print Payslip
                </button>
              </div>
            </div>

            {/* Payslip Document */}
            <div className={`payslip-doc ${styles['payslip-doc']}`}>
              
              {/* Company Header */}
              <div className={styles['company-header']}>
                <h1 className={styles['company-title']}>HARI OM ELECTRICALS</h1>
                <p className={styles['company-subtitle']}>Payslip for the month of <strong>{meta.period_label}</strong></p>
                <div className={`${styles['status-badge']} ${payroll.status === 'paid' ? styles['status-approved'] : payroll.status === 'approved' ? styles['status-partial'] : styles['status-pending']}`}>
                  {payroll.status}
                </div>
              </div>

              {/* Employee Info Grid */}
              <div className={styles['payslip-info-grid']}>
                <div>
                  <table className={styles['info-table']}>
                    <tbody>
                      <tr>
                        <td className={styles['info-label']}>Employee Name:</td>
                        <td className={styles['info-value']}>{payroll.employee?.employee_name}</td>
                      </tr>
                      <tr>
                        <td className={styles['info-label']}>Employee Code:</td>
                        <td style={{ fontWeight: '500', color: '#475569' }}>{payroll.employee?.employee_code}</td>
                      </tr>
                      <tr>
                        <td className={styles['info-label']}>Designation:</td>
                        <td>{payroll.employee?.designation?.designation_name || '-'}</td>
                      </tr>
                      <tr>
                        <td className={styles['info-label']}>Department:</td>
                        <td>{payroll.employee?.department?.departement_name || '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div>
                  <table className={styles['info-table']}>
                    <tbody>
                      <tr>
                        <td className={styles['info-label']}>Total Days:</td>
                        <td style={{ fontWeight: '500', color: '#475569' }}>{payroll.total_days_in_month}</td>
                      </tr>
                      <tr>
                        <td className={styles['info-label']}>Present/Working:</td>
                        <td>{payroll.present_days}</td>
                      </tr>
                      <tr>
                        <td className={styles['info-label']}>OT Days:</td>
                        <td>{payroll.ot_days}</td>
                      </tr>
                      <tr>
                        <td className={styles['info-label']}>Payroll Number:</td>
                        <td>{payroll.payroll_number}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Salary Details Grid */}
              <div className={styles['payslip-salary-grid']}>
                
                {/* Earnings */}
                <div className={styles['salary-table-card']}>
                  <div className={styles['salary-table-title']}>
                    Earnings
                  </div>
                  <table className={styles['salary-table']}>
                    <tbody>
                      <tr>
                        <td>Basic Salary (Allocated)</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(payroll.basic_salary)}</td>
                      </tr>
                      <tr>
                        <td>Earned Salary ({payroll.present_days} days)</td>
                        <td style={{ textAlign: 'right', fontWeight: '600' }}>{formatCurrency(payroll.earned_salary)}</td>
                      </tr>
                      {parseFloat(payroll.ot_amount) > 0 && (
                        <tr>
                          <td>Overtime Allowance</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(payroll.ot_amount)}</td>
                        </tr>
                      )}
                      <tr>
                        <td style={{ fontWeight: '700', borderTop: '1px solid #cbd5e1' }}>Gross Earnings</td>
                        <td style={{ textAlign: 'right', fontWeight: '800', borderTop: '1px solid #cbd5e1', color: '#0f172a' }}>
                          {formatCurrency(Math.max(0, parseFloat(payroll.gross_salary || 0)))}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Deductions */}
                <div className={styles['salary-table-card']}>
                  <div className={styles['salary-table-title']}>
                    Deductions
                  </div>
                  <table className={styles['salary-table']}>
                    <tbody>
                      {parseFloat(payroll.pf_deduction) > 0 && (
                        <tr>
                          <td>Provident Fund (PF)</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(payroll.pf_deduction)}</td>
                        </tr>
                      )}
                      {parseFloat(payroll.esic_deduction) > 0 && (
                        <tr>
                          <td>ESIC</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(payroll.esic_deduction)}</td>
                        </tr>
                      )}
                      {parseFloat(payroll.professional_tax) > 0 && (
                        <tr>
                          <td>Professional Tax</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(payroll.professional_tax)}</td>
                        </tr>
                      )}
                      {parseFloat(payroll.loan_emi_deduction) > 0 && (
                        <tr>
                          <td>Loan EMI Recovery</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(payroll.loan_emi_deduction)}</td>
                        </tr>
                      )}
                      {parseFloat(payroll.advance_deduction) > 0 && (
                        <tr>
                          <td>Advance Pay Recovery</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(payroll.advance_deduction)}</td>
                        </tr>
                      )}
                      {parseFloat(payroll.other_deduction) > 0 && (
                        <tr>
                          <td>Other Deductions <br/><span style={{ fontSize: '12px', color: '#64748b' }}>({payroll.other_deduction_remarks})</span></td>
                          <td style={{ textAlign: 'right', verticalAlign: 'top' }}>{formatCurrency(payroll.other_deduction)}</td>
                        </tr>
                      )}
                      <tr>
                        <td style={{ fontWeight: '700', borderTop: '1px solid #cbd5e1' }}>Total Deductions</td>
                        <td style={{ textAlign: 'right', fontWeight: '800', borderTop: '1px solid #cbd5e1', color: '#ef4444' }}>
                          {formatCurrency(Math.max(0, parseFloat(payroll.total_deductions || 0)))}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Net Pay */}
              <div className={styles['net-pay-card']}>
                <div>
                  <h3 className={styles['net-pay-title']}>Net Pay</h3>
                  <p className={styles['net-pay-subtitle']}>Gross Earnings - Total Deductions</p>
                </div>
                <div className={styles['net-pay-amount']}>
                  {formatCurrency(Math.max(0, parseFloat(payroll.net_salary || 0)))}
                </div>
              </div>

              {/* Signatures */}
              <div className={styles['signatures-grid']}>
                <div className={styles['signature-box']}>
                  <div className={styles['signature-line']}></div>
                  <p className={styles['signature-text']}>Employer Signature</p>
                </div>
                <div className={styles['signature-box']}>
                  <div className={styles['signature-line']}></div>
                  <p className={styles['signature-text']}>Employee Signature</p>
                </div>
              </div>

            </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .payslip-doc { box-shadow: none !important; margin: 0 !important; padding: 0 !important; }
          body { background: white !important; }
        }
      `}</style>

      {/* Action Modal */}
      {actionModal.open && (
        <div className={styles['modal-backdrop']} onClick={() => setActionModal({ open: false, type: '', remarks: '' })}>
          <div className={styles['modal-container']} onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className={styles['modal-header']}>
              <div className={styles['modal-title-group']}>
                <h2 style={{ textTransform: 'capitalize' }}>{actionModal.type} Payroll</h2>
                <p>Payroll #{payroll?.payroll_number || id} • {payroll?.employee?.name}</p>
              </div>
              <button 
                onClick={() => setActionModal({ open: false, type: '', remarks: '' })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <X size={20} />
              </button>
            </div>
            <div className={styles['modal-body']}>
              <p style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#334155' }}>
                Are you sure you want to <strong style={{ textTransform: 'lowercase', color: '#0f172a' }}>{actionModal.type}</strong> this payroll?
              </p>
              {actionModal.type === 'cancel' && (
                <div style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px' }}>
                  <p style={{ color: '#dc2626', fontSize: '13px', margin: 0, fontWeight: 500 }}>
                    ⚠️ This will revert any loan EMI and advance recovery deductions associated with this payroll.
                  </p>
                </div>
              )}
              <div style={{ marginTop: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
                  Remarks (Optional)
                </label>
                <textarea 
                  className={styles['modal-textarea']} 
                  rows="3" 
                  placeholder="Add any notes or remarks here..."
                  value={actionModal.remarks}
                  onChange={e => setActionModal(prev => ({ ...prev, remarks: e.target.value }))}
                />
              </div>
            </div>
            <div className={styles['modal-footer']}>
              <button 
                type="button"
                className={styles['btn-cancel']} 
                onClick={() => setActionModal({ open: false, type: '', remarks: '' })}
              >
                Cancel
              </button>
              <button 
                type="button"
                className={styles['btn-submit']} 
                onClick={handleAction}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {pdfPreviewUrl && (
        <div 
          className="modal-overlay" 
          onClick={closePdfPreview} 
          style={{ 
            position: 'fixed', 
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 9999, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            backgroundColor: 'rgba(0,0,0,0.7)' 
          }}
        >
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '80%', height: '90vh', display: 'flex', flexDirection: 'column', backgroundColor: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
            <div className="modal-header" style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>Payslip Preview</h2>
              <div style={{ display: 'flex', gap: '12px' }}>
                 <button className="modal-close" onClick={closePdfPreview} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                   <X size={24} />
                 </button>
              </div>
            </div>
            <div className="modal-body" style={{ flex: 1, padding: 0 }}>
              <iframe 
                src={pdfPreviewUrl} 
                title="PDF Preview"
                width="100%" 
                height="100%" 
                style={{ border: 'none' }}
              />
            </div>
          </div>
        </div>
      )}
      
          </div>
        </main>
        
        <Footer />
      </div>
    </div>
  );
};

export default ViewPayroll;
